import { useState, useContext } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearTiposPPE = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    tipcodigo: "",
    tipdescri: "",
    tipstatus: "A",
    tipaniodep: 0,
    tipmesdep: 0,
    tipporresidual: 0,
    tipctadepre: "",
    tipctagasdep: "",
    numsecini: "",
    numseccont: 0,
    tippervar: 0,
    tipinidepre: 0,
    tipanioutilsri: 0,
    tipmesutilsri: 0,
    tipctacon: "",
    tipproduccion: 0,
  })

  // Consulta al backend para obtener la lista de Cuentas Contables
  const { data: rawCuentas, isLoading: isLoadingCuentas } = useQuery({
    queryKey: ["cuentasContablesPPE"],
    queryFn: async () => {
      try {
        const response = await api.get("/TiposPPE/getCuentasContables")
        // AQUÍ ESTÁ LA MAGIA RESTAURADA: Buscamos el arreglo en todos los niveles posibles
        const dataExtraida = response?.data?.data || response?.data || response || []
        return dataExtraida
      } catch (error) {
        console.error("Error al cargar cuentas contables:", error)
        return []
      }
    },
  })

  // Protección estricta: garantizamos que listaCuentas sea un Array para que el combo no colapse
  const listaCuentas = Array.isArray(rawCuentas) ? rawCuentas : Array.isArray(rawCuentas?.data) ? rawCuentas.data : []

  const { mutateAsync: SaveNuevoTipoActivo, isPending: isSavingTipoActivo } = useMutation({
    queryKey: ["isCreatingTipoActivo"],
    fn: async (data) => {
      const response = await api.post("/TiposPPE/createTiposPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value

    if (field === "tipcodigo") finalValue = value.toUpperCase().slice(0, 4)
    else if (field === "tipdescri") finalValue = value.toUpperCase().slice(0, 100)
    else if (field === "numsecini") finalValue = value.slice(0, 3)
    else if (
      [
        "tipaniodep",
        "tipmesdep",
        "tipinidepre",
        "tipanioutilsri",
        "tipmesutilsri",
        "tipproduccion",
        "tippervar",
        "numseccont",
        "tipporresidual",
      ].includes(field)
    ) {
      finalValue = value === "" ? "" : Number(value)
    }

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    try {
      if (!formData.tipcodigo.trim()) {
        showWarning("El código de tipo de activo es obligatorio")
        return
      }
      if (!formData.tipdescri.trim()) {
        showWarning("La descripción es obligatoria")
        return
      }

      const payload = { ...formData }
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "") {
          if (
            ["tipcodigo", "tipdescri", "tipctadepre", "tipctagasdep", "tipctacon", "numsecini", "tipstatus"].includes(
              key,
            )
          ) {
            // Textos vacíos
          } else {
            payload[key] = 0
          }
        }
      })

      await SaveNuevoTipoActivo(payload)
    } catch (error) {
      console.error("Error al crear:", error)
    }
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GUARDAR" || action?.acccaption === "CREAR" || action?.acccaption === "NUEVO",
  )

  const toolbarActions = []
  if (guardarAction) {
    toolbarActions.push({
      label: guardarAction.acccaption,
      key: guardarAction.acccaption,
      icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
    })
  } else {
    toolbarActions.push({ label: "Guardar Tipo Activo", key: "GUARDAR", icon: <Save /> })
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingTipoActivo || isLoadingCuentas}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Tipo de Activo (PPE)</b>
        </div>

        <CustomBackdrop isLoading={isSavingTipoActivo || isLoadingCuentas} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos Generales
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.tipcodigo}
                  onChange={(e) => handleInputChange("tipcodigo", e.target.value)}
                  placeholder="Ej: 001"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.tipdescri}
                  onChange={(e) => handleInputChange("tipdescri", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.tipstatus}
                  onChange={(e) => handleInputChange("tipstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Parámetros de Depreciación
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Años a Depreciar"
                  value={formData.tipaniodep}
                  onChange={(e) => handleInputChange("tipaniodep", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Meses a Depreciar"
                  value={formData.tipmesdep}
                  onChange={(e) => handleInputChange("tipmesdep", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="% Valor Residual"
                  value={formData.tipporresidual}
                  onChange={(e) => handleInputChange("tipporresidual", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Inicio Depreciación"
                  value={formData.tipinidepre}
                  onChange={(e) => handleInputChange("tipinidepre", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Período Variación"
                  value={formData.tippervar}
                  onChange={(e) => handleInputChange("tippervar", e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Integración Contable
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCuentas}
                  getOptionLabel={(option) => option.label || ""}
                  value={listaCuentas.find((c) => c.id === formData.tipctacon) || null}
                  onChange={(event, newValue) => handleInputChange("tipctacon", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cta. Contable Activo"
                      placeholder="Buscar cuenta..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCuentas}
                  getOptionLabel={(option) => option.label || ""}
                  value={listaCuentas.find((c) => c.id === formData.tipctadepre) || null}
                  onChange={(event, newValue) => handleInputChange("tipctadepre", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cta. Depreciación Acumulada"
                      placeholder="Buscar cuenta..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCuentas}
                  getOptionLabel={(option) => option.label || ""}
                  value={listaCuentas.find((c) => c.id === formData.tipctagasdep) || null}
                  onChange={(event, newValue) => handleInputChange("tipctagasdep", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cta. Gasto Depreciación"
                      placeholder="Buscar cuenta..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Configuraciones Adicionales y SRI
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Años Vida Útil SRI"
                  value={formData.tipanioutilsri}
                  onChange={(e) => handleInputChange("tipanioutilsri", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Meses Vida Útil SRI"
                  value={formData.tipmesutilsri}
                  onChange={(e) => handleInputChange("tipmesutilsri", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Núm. Sec. Inicial"
                  value={formData.numsecini}
                  onChange={(e) => handleInputChange("numsecini", e.target.value)}
                />
              </Grid>
              {/* AQUÍ AGREGAMOS EL CAMPO FALTANTE */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Núm. Secuencia Contable"
                  value={formData.numseccont}
                  onChange={(e) => handleInputChange("numseccont", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="number"
                  label="Producción"
                  value={formData.tipproduccion}
                  onChange={(e) => handleInputChange("tipproduccion", e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearTiposPPE
